import React, { Fragment, useEffect, useMemo } from 'react'
import { FieldError, useFieldArray, UseFormReturn } from 'react-hook-form'
import { CreateProductOrderDto, ProductCatalogDto, ProductType, ProductValidationRuleOperator } from '@services/dtos'
import InputField from '@components/inputs/InputField'
import { DateInputField } from '@components/inputs/DateInputField'
import moment from 'moment'
import SelectField from '@components/inputs/SelectField'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Alert, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, Typography } from '@mui/material'
import { MemberDto, Site } from '@services/api'
import { WrapperFormContent } from '../../WrapperFormContent'
import { findSiteAddresses } from '@state/thunks/siteThunk'
import { clearAddresses } from '@state/reducers/siteReducer'
import { MemberRoleEnumLabels } from '@utils/constants'
import { SimpleFileInput } from '@components/inputs/SimpleFileInput'
import { setProductOrderFile } from '@state/reducers/productOrderReducer'
import SelectSupplierEmail from '@components/SelectSupplierEmail'
import { ProductOrderGeneratorCalendar } from '../components/ProductOrderGeneratorCalendar'

type Props = {
  useCreateForm: UseFormReturn<CreateProductOrderDto, object>
}

const ProductOrderStepInfo: React.FC<Props> = ({ useCreateForm }) => {
  const { setValue, formState: { errors }, watch, control } = useCreateForm
  const { fields: lineItems } = useFieldArray({
    control,
    name: 'lineItems',
  })
  const { user, siteAddresses, loadingAddresses, selectedProducts, filesByProductId, selectedSupplier } = useAppSelector(
    ({ auth, site, productOrder, supplier }) => ({
      user: auth.user,
      siteAddresses: site.addresses,
      loadingAddresses: site.loadingAddresses,
      selectedProducts: productOrder.selectedProducts,
      filesByProductId: productOrder.filesByProductId,
      selectedSupplier: supplier.items.datas.find(s => s.id === watch('supplierId')) || null,
  }))

  const dispatch = useAppDispatch()

  const sites = useMemo(() => (user as any & { sites: Site[] })?.sites, [user])

  useEffect(() => {
    if (sites?.length === 1) {
      setValue('siteId', sites[0].id)
    }
  }, [sites, setValue])
  const siteId = watch('siteId')
  useEffect(() => {
    if (siteId) {
      dispatch(clearAddresses())
      dispatch(findSiteAddresses(siteId))
    }
  }, [dispatch, siteId])
  useEffect(() => {
    if (!loadingAddresses && siteAddresses.length === 1) {
      setValue('shippingAddressId', siteAddresses[0].id)
    }
  }, [loadingAddresses, siteAddresses, setValue])

  const shippingAddressId = watch('shippingAddressId')

  const productsWithValidation = selectedProducts.filter(({ validation }) => !!validation)

  const ProductValidationBlock = ({ product }: { product: ProductCatalogDto }) => {
    const { name, validation } = product

    const joinMembers = (
      members: MemberDto[],
      operator: ProductValidationRuleOperator
    ) => {
      const label = operator === 'AND' ? ' et ' : ' ou '
      const names = members.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
      if (names.length <= 1) return names[0] ?? ''
      if (names.length === 2) return names.join(label)
      return names.slice(0, -1).join(', ') + label + names[names.length - 1]
    }

    return (
      <div className='mt-4'>
        <Typography fontWeight={500}>
          Pour {name} :
        </Typography>

        {validation?.rules.map((rule, index) => {
          const roleLabel = MemberRoleEnumLabels[rule.role] ?? rule.role
          const members = joinMembers(rule.members, rule.operator)

          return (
            <Fragment key={index}>
              <Typography variant="body2">
                • Validation {roleLabel} : {members}
              </Typography>

              {index < validation.rules.length - 1 && (
                <Typography variant="body2" color="text.secondary">
                  {validation?.groupOperator === 'AND' ? 'ET' : 'OU'}
                </Typography>
              )}
            </Fragment>
          )
        })}
      </div>
  )}

  const displayGeneratorCalendar = selectedProducts.some(({ type }) => type === ProductType.GENERATOR)

  return (
    <Grid container spacing={2}>
      <Grid container item xs={12} lg={6}>
        <Grid item xs={12}>
          <WrapperFormContent legend={'Livraison'} className="h-full">
            <Grid container spacing={2} className="mb-4">
              <Grid item xs={12}>
                {sites?.length ? (
                  <SelectField
                    data-cy="select-site"
                    key={siteId}
                    control={control}
                    name="siteId"
                    options={sites.map(({ id, label }) => ({
                      label,
                      value: id,
                    }))}
                    value={siteId}
                    label="Site"
                  />
                ) : (
                  <Grid item xs={12}><Typography variant="body1">Aucun site enregistré</Typography></Grid>
                )}
              </Grid>
              {siteId && !loadingAddresses && (siteAddresses.length > 0 ? (
                <Grid item xs={12}>
                  <SelectField
                    data-cy="select-shipping-address"
                    key={shippingAddressId}
                    control={control}
                    name="shippingAddressId"
                    options={siteAddresses.map(({ id, streetAddress, postalCode, city }) => ({
                      label: `${streetAddress}, ${postalCode} ${city}`,
                      value: id,
                    }))}
                    value={shippingAddressId}
                    label="Adresse de livraison"
                  />
                </Grid>
              ) : <Grid item xs={12}>
                <Typography variant="body1">Aucune adresse enregistrée pour ce site</Typography>
              </Grid>)}

              <Grid item xs={12}>
                <InputField
                  control={useCreateForm.control}
                  name={'deliveryInstructions'}
                  label={'Instructions de livraison'}
                  type={'text'}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid container item xs={12}>
                {lineItems.map((item, index) => {
                  const product = selectedProducts.find(p => p.id === item.productId)
                  const itemError = errors.lineItems?.[index] as unknown as FieldError
                  const isStandardDelay = watch(`lineItems.${index}.isStandardDelay`)

                  return (
                    <Grid container spacing={2} key={item.id} className="!mt-1" data-testid={`delivery-product-${index+1}`}>
                      <Grid item xs={12}>
                        <Typography fontWeight={500}>
                          {product?.name}
                        </Typography>
                        {product?.supplyLeadTimeDays && (
                          <Typography variant="body2" color="text.secondary">
                            {'Délai d\'approvisionnement indicatif : '}{product.supplyLeadTimeDays} jour{product.supplyLeadTimeDays > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {product?.type && product.type !== ProductType.GENERATOR ? (
                          <FormControl
                            fullWidth
                            error={!!errors.lineItems?.[index]?.desiredDeliveryDate}
                          >
                            <DateInputField
                              label="Date de livraison souhaitée"
                              name={`lineItems.${index}.desiredDeliveryDate`}
                              value={
                                item.desiredDeliveryDate
                                  ? moment(item.desiredDeliveryDate)
                                  : null
                              }
                              disablePast
                              disabled={isStandardDelay === true}
                              onChange={(date) => {
                                setValue(
                                  `lineItems.${index}.desiredDeliveryDate`,
                                  date ? date.format('YYYY-MM-DD') : undefined
                                )
                                if (date) {
                                  setValue(`lineItems.${index}.isStandardDelay`, false)
                                }
                              }}
                              sx={{
                                border: errors.lineItems?.[index]?.desiredDeliveryDate
                                  ? '1px solid #d3302f'
                                  : 'none',
                                borderRadius: '13px',
                              }}
                            />
                            {errors.lineItems?.[index]?.desiredDeliveryDate && (
                              <FormHelperText error>
                                {errors.lineItems[index].desiredDeliveryDate?.message}
                              </FormHelperText>
                            )}
                            {itemError && (
                              <FormHelperText error>
                                {itemError.message}
                              </FormHelperText>
                            )}
                          </FormControl>) : (
                          <>
                            <Typography variant="body2">
                              Bon de commande GE
                            </Typography>
                            <SimpleFileInput
                              canRemove={false}
                              file={filesByProductId[item.productId]}
                              onSelect={(file) => {
                                if (!file) {
                                  return
                                }
                                dispatch(setProductOrderFile({
                                  productId: item.productId,
                                  file,
                                }))
                              }}
                              error={itemError?.message}
                            />
                          </>
                        )}
                      </Grid>
                      {product?.type && product.type !== ProductType.GENERATOR && (
                        <Grid item xs={12} sm={6} className="flex items-center">
                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid={'checkbox-standard-delay-product'}
                                checked={!!isStandardDelay}
                                onChange={(e) => {
                                const checked = e.target.checked
                                setValue(`lineItems.${index}.isStandardDelay`, checked)
                                if (checked) {
                                  setValue(`lineItems.${index}.desiredDeliveryDate`, undefined)
                                }
                              }}
                            />
                          }
                          label="Délai standard"
                        />
                      </Grid>)}
                    </Grid>
                  )
                })}
                {displayGeneratorCalendar && (<div className="mt-4 w-full">
                  <Typography fontWeight={500} className="w-full">
                    Dates prévisionnelles des générateurs
                  </Typography>
                  <ProductOrderGeneratorCalendar
                    generators={
                      watch('lineItems')
                        .filter(({ productId }) => selectedProducts.find(({ id }) => id === productId)?.type === ProductType.GENERATOR)
                        .map(({ productId, quantity, plannedDates }) => ({
                          product: selectedProducts.find(({ id }) => id === productId),
                          quantity,
                          plannedDates,
                        }))
                        .filter((item): item is { product: ProductCatalogDto; quantity: number, plannedDates: { date: string; quantity: number }[] } => !!item.product)
                    }
                    addPlannedDate={(productId, plannedDate, quantity) => {
                      const lineItemIndex = lineItems.findIndex(item => item.productId === productId)
                      if (lineItemIndex === -1) {
                        return
                      }
                      const currentDates = watch(`lineItems.${lineItemIndex}.plannedDates`) || []
                      const existingIndex = currentDates.findIndex(({ date }) => date === plannedDate)
                      if (existingIndex !== -1) {
                        const updated = [...currentDates]
                        updated[existingIndex] = {
                          ...updated[existingIndex],
                          quantity: updated[existingIndex].quantity + quantity,
                        }
                        setValue(`lineItems.${lineItemIndex}.plannedDates`, updated)
                        return
                      }
                      setValue(`lineItems.${lineItemIndex}.plannedDates`, [
                        ...currentDates,
                        { date: plannedDate, quantity },
                      ])
                    }}
                    removePlannedDate={(dateKey, productId) => {
                      const lineItemIndex = lineItems.findIndex(item => item.productId === productId)
                      if (lineItemIndex === -1) {
                        return
                      }
                      const currentDates = watch(`lineItems.${lineItemIndex}.plannedDates`) || []
                      const updated = currentDates.filter(({ date }) => date !== dateKey)
                      setValue(`lineItems.${lineItemIndex}.plannedDates`, updated)
                    }}
                  />
                </div>)}
              </Grid>
            </Grid>
          </WrapperFormContent>
        </Grid>
      </Grid>

      <Grid container item xs={12} lg={6}>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
          <WrapperFormContent legend={'Email commande'} className="mb-3">
            {selectedSupplier ? <SelectSupplierEmail
              supplier={selectedSupplier}
              control={control}
            /> : <Typography variant="body2" color="textSecondary">Aucun fournisseur sélectionné</Typography>}
          </WrapperFormContent>
          <WrapperFormContent legend={'Validation'} className="flex-1">
            {productsWithValidation.length > 0 ? (
              <div className="overflow-y-auto">
                <Alert severity="info">
                  Des validations sont nécessaires pour {productsWithValidation.length} produit{productsWithValidation.length > 1 ? 's' : ''} de cette commande.<br />
                  La commande sera transmise au fournisseur une fois que les membres concernés auront approuvé.
                </Alert>
              
                {productsWithValidation.map(product => (
                  <ProductValidationBlock key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <Alert severity="success">
                {'Aucune validation n\'est nécessaire pour les produits de cette commande.'}
              </Alert>
            )}
          </WrapperFormContent>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ProductOrderStepInfo
